import { useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import "./TypingIndicator.scss";

const getBadgeClass = (score) => {
  const num = parseInt(score);
  if (num >= 95) return "badge-green";
  if (num >= 90) return "badge-yellow";
  return "badge-red";
};

const BORDER_COLORS = ["#4F46E5", "#059669", "#0284C7", "#7C3AED", "#EA580C"];

const tagColorPalette = [
  { bg: "#e0e7ff", color: "#bb1ccc", border: "#c7d2fe" },
  { bg: "#fce7f3", color: "#be185d", border: "#f9a8d4" },
  { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" },
  { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" },
  { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7" },
  { bg: "#e9d5ff", color: "#6b21a8", border: "#c084fc" },
  { bg: "#fed7aa", color: "#9a3412", border: "#fdba74" },
  { bg: "#fecaca", color: "#991b1b", border: "#fca5a5" },
  { bg: "#cffafe", color: "#164e63", border: "#67e8f9" },
  { bg: "#e5e7eb", color: "#374151", border: "#9ca3af" },
];

const getTagColor = (index) => tagColorPalette[index % tagColorPalette.length];

const MAX_VISIBLE_TAGS = 3;

function AssetCard({ card, borderColor }) {
  const tags = Array.isArray(card.tags) ? card.tags : [];

  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const remainingTags = tags.slice(MAX_VISIBLE_TAGS);
  const remainingCount = remainingTags.length;

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl px-6 py-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 border-l-[4px]"
      style={{ borderLeftColor: borderColor }}
    >
      {/* Title and Badge */}
      <div className="flex items-start justify-between gap-3 mb-1">
       <a
  href={`/dataDomain?${new URLSearchParams({
    l1: card.path.split("→")[0]?.trim(),
    l2: card.path.split("→")[1]?.trim(),
    l3: card.path.split("→")[2]?.trim(),
    l4: card.path.split("→")[3]?.trim(),
    l5: card.title
  }).toString()}`}
  className="font-mono text-[13px] font-bold text-blue-600 break-all hover:underline"
  target="_blank"
  rel="noopener noreferrer"
>
  {card.title?.split("|").join(" · ")}
</a>
        <span
          className={`asset-card-badge text-[12px] font-bold px-3 py-1.5 rounded-lg whitespace-nowrap shrink-0 ${getBadgeClass(card.score_badge)}`}
        >
          {card.score_badge}
        </span>
      </div>

      {/* Path */}
      <div className="text-[12px] text-[#bb1ccc] font-semibold mb-3 flex items-center gap-1">
        <span
          className="inline-block !w-2 !h-2 rounded-full shrink-0 opacity-50"
          style={{ background: borderColor }}
        />
        {card.path}
      </div>

      {/* Description */}
      <p className="text-[13px] leading-relaxed mb-4 text-black">
        {card.description}
      </p>

      {/* Tags */}
      <div className="flex items-center gap-2 flex-wrap">
        {tags.length === 0 ? (
          <span className="text-gray-400 text-xs italic">N/A</span>
        ) : (
          <>
            {visibleTags.map((tag, index) => {
              const tagColor = getTagColor(index);
              return (
                <span
                  key={index}
                  className="px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
                  style={{
                    backgroundColor: tagColor.bg,
                    color: tagColor.color,
                    borderColor: tagColor.border,
                  }}
                >
                  {tag}
                </span>
              );
            })}

            {remainingCount > 0 && (
              <Tooltip
                title={remainingTags.join(", ")}
                placement="top"
                arrow
              >
                <span
                  className="px-3 py-1 rounded-full text-xs font-semibold border cursor-pointer
                             bg-blue-100 text-blue-800 border-blue-200
                             hover:bg-blue-200 hover:border-blue-800 select-none"
                >
                  +{remainingCount}
                </span>
              </Tooltip>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function AssetCardResponse({ cards }) {
  if (!Array.isArray(cards)) return null;

  return (
    <div className="flex flex-col gap-3 mt-2 w-full">
      {cards.map((card, i) => {
        const borderColor = BORDER_COLORS[i % BORDER_COLORS.length];
        return <AssetCard key={i} card={card} borderColor={borderColor} />;
      })}
    </div>
  );
}